import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { forecastsApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Save,
  RefreshCw,
  Target,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Calendar,
  Building2,
  ChevronDown,
  ChevronRight,
  Percent,
  Calculator,
  Wand2,
  CheckCircle2,
  Archive,
  Layers,
  Grid3X3,
  Search,
  RotateCcw
} from 'lucide-react';

const MONTHS = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
const MONTH_KEYS = ['jul', 'aug', 'sep', 'oct', 'nov', 'dec', 'jan', 'feb', 'mar', 'apr', 'may', 'jun'];

// Quarters for fiscal year (Jul-Jun)
const QUARTERS = [
  { key: 'q1', label: 'Q1', months: ['Jul', 'Aug', 'Sep'], monthKeys: ['jul', 'aug', 'sep'] },
  { key: 'q2', label: 'Q2', months: ['Oct', 'Nov', 'Dec'], monthKeys: ['oct', 'nov', 'dec'] },
  { key: 'q3', label: 'Q3', months: ['Jan', 'Feb', 'Mar'], monthKeys: ['jan', 'feb', 'mar'] },
  { key: 'q4', label: 'Q4', months: ['Apr', 'May', 'Jun'], monthKeys: ['apr', 'may', 'jun'] }
];

const FORECAST_TYPES = [
  { value: 'base', label: 'Base Case', icon: Target, color: 'emerald' },
  { value: 'best_case', label: 'Best Case', icon: TrendingUp, color: 'blue' },
  { value: 'worst_case', label: 'Worst Case', icon: BarChart3, color: 'amber' },
  { value: 'rolling', label: 'Rolling Forecast', icon: RefreshCw, color: 'purple' }
];

const CATEGORIES = ['Sales', 'Purchases', 'Overhead', 'Salaries', 'Management Fees', 'Other Expenses'];

const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'R 0.00';
  const absValue = Math.abs(value);
  const formatted = new Intl.NumberFormat('en-ZA', { 
    style: 'currency', 
    currency: 'ZAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(absValue);
  return value < 0 ? `(${formatted})` : formatted;
};

const ForecastDetailPage = () => {
  const { forecastId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { isDark } = useTheme();
  
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedLineItems, setEditedLineItems] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  
  // View mode: 'monthly' or 'quarterly'
  const [viewMode, setViewMode] = useState('monthly');
  const [activeQuarter, setActiveQuarter] = useState('q1');
  
  // Expanded categories
  const [expandedCategories, setExpandedCategories] = useState(new Set(['Sales']));
  
  // Adjustment dialog
  const [adjustmentDialog, setAdjustmentDialog] = useState(false);
  const [adjustmentTab, setAdjustmentTab] = useState('category'); // 'category' or 'subaccount'
  const [adjustmentData, setAdjustmentData] = useState({
    type: 'percentage',
    value: 0,
    categories: [],
    months: [],
    quarters: []
  });
  // Category-level adjustments (each category has its own type and value)
  const [categoryAdjustments, setCategoryAdjustments] = useState({});
  // Sub-account level adjustments
  const [subAccountAdjustments, setSubAccountAdjustments] = useState({});
  const [subAccountSearch, setSubAccountSearch] = useState('');
  const [expandedAdjustmentCategories, setExpandedAdjustmentCategories] = useState(new Set(['Sales']));
  
  useEffect(() => {
    loadForecast();
  }, [forecastId]);
  
  const loadForecast = async () => {
    try {
      setLoading(true);
      const res = await forecastsApi.getById(forecastId);
      setForecast(res.data);
      setEditedLineItems(res.data.line_items || []);
    } catch (error) {
      console.error('Failed to load forecast:', error);
      toast.error('Failed to load forecast');
      navigate('/forecasts');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLineItemChange = (itemId, month, value) => {
    setEditedLineItems(prev => prev.map(item => {
      if (item.id === itemId) {
        return { ...item, [month]: parseFloat(value) || 0 };
      }
      return item;
    }));
    setHasChanges(true);
  };
  
  // Apply quarterly growth rate to all months in a quarter
  const handleQuarterlyGrowthChange = (itemId, quarterKey, growthPercent) => {
    const quarter = QUARTERS.find(q => q.key === quarterKey);
    if (!quarter) return;
    
    setEditedLineItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item };
        const baseValue = item[quarter.monthKeys[0]] || 0;
        
        quarter.monthKeys.forEach(monthKey => {
          const currentVal = item[monthKey] || baseValue;
          updatedItem[monthKey] = currentVal * (1 + growthPercent / 100);
        });
        
        return updatedItem;
      }
      return item;
    }));
    setHasChanges(true);
  };
  
  const handleSave = async () => {
    try {
      setSaving(true);
      await forecastsApi.updateLineItems(forecastId, editedLineItems);
      toast.success('Forecast saved successfully');
      setHasChanges(false);
      await loadForecast();
    } catch (error) {
      console.error('Failed to save forecast:', error);
      toast.error('Failed to save forecast');
    } finally {
      setSaving(false);
    }
  };
  
  const handleApplyAdjustment = async () => {
    try {
      setSaving(true);
      
      // Get months to update based on quarters selection
      let monthsToUpdate = [];
      if (adjustmentData.quarters.length > 0) {
        adjustmentData.quarters.forEach(qKey => {
          const quarter = QUARTERS.find(q => q.key === qKey);
          if (quarter) {
            monthsToUpdate.push(...quarter.monthKeys);
          }
        });
      }
      if (monthsToUpdate.length === 0) {
        monthsToUpdate = MONTH_KEYS;
      }
      
      let updatedItems;
      
      if (adjustmentTab === 'category') {
        // Apply category-level adjustments
        updatedItems = editedLineItems.map(item => {
          const categoryAdj = categoryAdjustments[item.category];
          if (!categoryAdj || categoryAdj.type === 'none' || categoryAdj.value === 0) {
            return item;
          }
          
          const updatedItem = { ...item };
          monthsToUpdate.forEach(monthKey => {
            const currentVal = item[monthKey] || 0;
            if (categoryAdj.type === 'percentage') {
              updatedItem[monthKey] = currentVal * (1 + categoryAdj.value / 100);
            } else {
              updatedItem[monthKey] = currentVal + categoryAdj.value;
            }
          });
          
          return updatedItem;
        });
      } else {
        // Apply sub-account level adjustments
        updatedItems = editedLineItems.map(item => {
          const itemAdj = subAccountAdjustments[item.id];
          if (!itemAdj || itemAdj.type === 'none' || itemAdj.value === 0) {
            return item;
          }
          
          const updatedItem = { ...item };
          monthsToUpdate.forEach(monthKey => {
            const currentVal = item[monthKey] || 0;
            if (itemAdj.type === 'percentage') {
              updatedItem[monthKey] = currentVal * (1 + itemAdj.value / 100);
            } else {
              updatedItem[monthKey] = currentVal + itemAdj.value;
            }
          });
          
          return updatedItem;
        });
      }
      
      setEditedLineItems(updatedItems);
      setHasChanges(true);
      
      toast.success('Adjustment applied - remember to save changes');
      setAdjustmentDialog(false);
      resetAdjustmentState();
    } catch (error) {
      console.error('Failed to apply adjustment:', error);
      toast.error('Failed to apply adjustment');
    } finally {
      setSaving(false);
    }
  };
  
  const resetAdjustmentState = () => {
    setAdjustmentTab('category');
    setAdjustmentData({ type: 'percentage', value: 0, categories: [], months: [], quarters: [] });
    setCategoryAdjustments({});
    setSubAccountAdjustments({});
    setSubAccountSearch('');
  };
  
  const initializeCategoryAdjustments = () => {
    const initial = {};
    CATEGORIES.forEach(cat => {
      initial[cat] = { type: 'percentage', value: 0 };
    });
    setCategoryAdjustments(initial);
  };
  
  const toggleAdjustmentCategory = (category) => {
    const newExpanded = new Set(expandedAdjustmentCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedAdjustmentCategories(newExpanded);
  };
  
  // Filter sub-accounts based on search
  const filteredSubAccounts = useMemo(() => {
    if (!subAccountSearch) return editedLineItems;
    const query = subAccountSearch.toLowerCase();
    return editedLineItems.filter(item => 
      item.account_name?.toLowerCase().includes(query) ||
      item.account_code?.toLowerCase().includes(query) ||
      item.name?.toLowerCase().includes(query)
    );
  }, [editedLineItems, subAccountSearch]);
  
  const handleStatusChange = async (newStatus) => {
    try {
      await forecastsApi.updateStatus(forecastId, newStatus);
      toast.success(`Forecast ${newStatus === 'active' ? 'activated' : newStatus === 'archived' ? 'archived' : 'set to draft'}`);
      await loadForecast();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };
  
  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };
  
  // Group line items by category
  const groupedItems = useMemo(() => {
    const grouped = {};
    CATEGORIES.forEach(cat => { grouped[cat] = []; });
    
    editedLineItems.forEach(item => {
      const cat = item.category || 'Other Expenses';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(item);
    });
    
    return grouped;
  }, [editedLineItems]);
  
  // Calculate totals
  const totals = useMemo(() => {
    const result = {
      revenue: 0,
      expenses: 0,
      monthly: {},
      quarterly: {}
    };
    
    MONTH_KEYS.forEach(m => {
      result.monthly[m] = { revenue: 0, expenses: 0 };
    });
    
    QUARTERS.forEach(q => {
      result.quarterly[q.key] = { revenue: 0, expenses: 0, net: 0 };
    });
    
    editedLineItems.forEach(item => {
      const isRevenue = item.category === 'Sales';
      MONTH_KEYS.forEach((m, idx) => {
        const val = item[m] || 0;
        const quarterKey = QUARTERS[Math.floor(idx / 3)].key;
        
        if (isRevenue) {
          result.revenue += val;
          result.monthly[m].revenue += val;
          result.quarterly[quarterKey].revenue += val;
        } else {
          result.expenses += val;
          result.monthly[m].expenses += val;
          result.quarterly[quarterKey].expenses += val;
        }
      });
    });
    
    // Calculate quarterly net
    QUARTERS.forEach(q => {
      result.quarterly[q.key].net = result.quarterly[q.key].revenue - result.quarterly[q.key].expenses;
    });
    
    return result;
  }, [editedLineItems]);
  
  const typeInfo = forecast ? FORECAST_TYPES.find(t => t.value === forecast.forecast_type) || FORECAST_TYPES[0] : FORECAST_TYPES[0];
  const TypeIcon = typeInfo.icon;
  
  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64 rounded-sm" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-sm" />)}
        </div>
        <Skeleton className="h-96 rounded-sm" />
      </div>
    );
  }
  
  if (!forecast) return null;
  
  return (
    <div className={`p-6 space-y-6 ${isDark ? 'bg-zinc-950 text-white' : 'bg-slate-50 text-slate-900'}`} data-testid="forecast-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/forecasts')} className="rounded-sm">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className={`text-2xl font-display font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {forecast.name}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <span className={`flex items-center gap-1 text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                <Building2 className="w-3 h-3" />
                {forecast.business_unit_name}
              </span>
              <span className={`text-sm ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>•</span>
              <span className={`flex items-center gap-1 text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                <Calendar className="w-3 h-3" />
                {forecast.fiscal_year}
              </span>
              <span className={`px-2 py-0.5 text-xs rounded-sm font-medium ${
                forecast.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                forecast.status === 'draft' ? 'bg-slate-100 text-slate-700' :
                'bg-zinc-100 text-zinc-700'
              }`}>
                {forecast.status}
              </span>
              <span className={`px-2 py-0.5 text-xs rounded-sm font-medium bg-${typeInfo.color}-100 text-${typeInfo.color}-700`}>
                {typeInfo.label}
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => setAdjustmentDialog(true)} 
            className="rounded-sm"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            Apply Adjustment
          </Button>
          
          {forecast.status === 'draft' && (
            <Button 
              variant="outline" 
              onClick={() => handleStatusChange('active')} 
              className="rounded-sm"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Activate
            </Button>
          )}
          
          {forecast.status === 'active' && (
            <Button 
              variant="outline" 
              onClick={() => handleStatusChange('archived')} 
              className="rounded-sm"
            >
              <Archive className="w-4 h-4 mr-2" />
              Archive
            </Button>
          )}
          
          <Button 
            onClick={handleSave} 
            disabled={saving || !hasChanges}
            className="bg-emerald-600 hover:bg-emerald-700 rounded-sm"
          >
            {saving ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" />Save Changes</>
            )}
          </Button>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
          <CardContent className="p-4">
            <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Total Revenue</p>
            <p className="font-mono text-xl font-bold text-emerald-600 mt-1">
              {formatCurrency(totals.revenue)}
            </p>
          </CardContent>
        </Card>
        
        <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
          <CardContent className="p-4">
            <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Total Expenses</p>
            <p className="font-mono text-xl font-bold text-red-600 mt-1">
              {formatCurrency(totals.expenses)}
            </p>
          </CardContent>
        </Card>
        
        <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
          <CardContent className="p-4">
            <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Net Income</p>
            <p className={`font-mono text-xl font-bold ${totals.revenue - totals.expenses >= 0 ? 'text-emerald-600' : 'text-red-600'} mt-1`}>
              {formatCurrency(totals.revenue - totals.expenses)}
            </p>
          </CardContent>
        </Card>
        
        <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
          <CardContent className="p-4">
            <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Profit Margin</p>
            <p className="font-mono text-xl font-bold text-violet-600 mt-1">
              {totals.revenue > 0 ? ((totals.revenue - totals.expenses) / totals.revenue * 100).toFixed(1) : 0}%
            </p>
          </CardContent>
        </Card>
        
        <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
          <CardContent className="p-4">
            <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>View Mode</p>
            <div className="flex items-center gap-2 mt-2">
              <Button
                size="sm"
                variant={viewMode === 'monthly' ? 'default' : 'outline'}
                onClick={() => setViewMode('monthly')}
                className={`rounded-sm h-7 text-xs ${viewMode === 'monthly' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
              >
                Monthly
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'quarterly' ? 'default' : 'outline'}
                onClick={() => setViewMode('quarterly')}
                className={`rounded-sm h-7 text-xs ${viewMode === 'quarterly' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
              >
                Quarterly
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Quarterly Summary Bar */}
      {viewMode === 'quarterly' && (
        <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Grid3X3 className="w-4 h-4 text-violet-600" />
                <span className="font-medium">Quarterly Overview</span>
              </div>
              <div className="flex items-center gap-1">
                {QUARTERS.map(q => (
                  <Button
                    key={q.key}
                    size="sm"
                    variant={activeQuarter === q.key ? 'default' : 'outline'}
                    onClick={() => setActiveQuarter(q.key)}
                    className={`rounded-sm h-8 ${activeQuarter === q.key ? 'bg-violet-600 hover:bg-violet-700' : ''}`}
                    data-testid={`quarter-tab-${q.key}`}
                  >
                    {q.label}
                    <span className={`ml-2 text-xs ${activeQuarter === q.key ? 'opacity-80' : 'opacity-60'}`}>
                      ({q.months[0]}-{q.months[2]})
                    </span>
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Quarterly Summary */}
            <div className="grid grid-cols-4 gap-4 mt-4">
              {QUARTERS.map(q => {
                const qData = totals.quarterly[q.key];
                const isActive = activeQuarter === q.key;
                return (
                  <div 
                    key={q.key}
                    onClick={() => setActiveQuarter(q.key)}
                    className={`p-3 rounded-sm border cursor-pointer transition-all ${
                      isActive 
                        ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20' 
                        : isDark ? 'border-zinc-700 hover:border-zinc-600' : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`font-medium ${isActive ? 'text-violet-700 dark:text-violet-400' : ''}`}>{q.label}</span>
                      <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                        {q.months[0]}-{q.months[2]}
                      </span>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className={isDark ? 'text-zinc-400' : 'text-slate-500'}>Revenue</span>
                        <span className="font-mono text-emerald-600">{formatCurrency(qData.revenue)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className={isDark ? 'text-zinc-400' : 'text-slate-500'}>Expenses</span>
                        <span className="font-mono text-red-600">{formatCurrency(qData.expenses)}</span>
                      </div>
                      <div className={`flex justify-between text-xs pt-1 border-t ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                        <span className="font-medium">Net</span>
                        <span className={`font-mono font-medium ${qData.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatCurrency(qData.net)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Line Items by Category */}
      <div className="space-y-4">
        {CATEGORIES.map(category => {
          const items = groupedItems[category] || [];
          if (items.length === 0) return null;
          
          const isExpanded = expandedCategories.has(category);
          const categoryTotal = items.reduce((sum, item) => {
            const monthsToSum = viewMode === 'quarterly' 
              ? QUARTERS.find(q => q.key === activeQuarter)?.monthKeys || []
              : MONTH_KEYS;
            return sum + monthsToSum.reduce((s, m) => s + (item[m] || 0), 0);
          }, 0);
          
          // Get months to display based on view mode
          const displayMonths = viewMode === 'quarterly'
            ? QUARTERS.find(q => q.key === activeQuarter)?.monthKeys || []
            : MONTH_KEYS;
          const displayMonthLabels = viewMode === 'quarterly'
            ? QUARTERS.find(q => q.key === activeQuarter)?.months || []
            : MONTHS;
          
          return (
            <Card key={category} className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
              <CardHeader 
                className={`py-3 border-b cursor-pointer ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}
                onClick={() => toggleCategory(category)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="font-display text-base font-medium flex items-center gap-2">
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    {category}
                    <span className={`text-xs px-2 py-0.5 rounded-sm ${isDark ? 'bg-zinc-800 text-zinc-400' : 'bg-slate-100 text-slate-500'}`}>
                      {items.length} items
                    </span>
                    {viewMode === 'quarterly' && (
                      <Badge variant="outline" className="text-xs ml-2">
                        {QUARTERS.find(q => q.key === activeQuarter)?.label}
                      </Badge>
                    )}
                  </CardTitle>
                  <span className={`font-mono text-sm font-medium ${category === 'Sales' ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(categoryTotal)}
                  </span>
                </div>
              </CardHeader>
              
              {isExpanded && (
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={isDark ? 'bg-zinc-800/50' : 'bg-slate-50'}>
                        <th className={`py-2 px-4 text-left font-medium min-w-[200px] ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                          Account
                        </th>
                        {displayMonthLabels.map((month, idx) => (
                          <th key={month} className={`py-2 px-2 text-right font-medium min-w-[100px] ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                            {month}
                          </th>
                        ))}
                        <th className={`py-2 px-4 text-right font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                          {viewMode === 'quarterly' ? 'Q Total' : 'Total'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => {
                        const itemTotal = displayMonths.reduce((sum, m) => sum + (item[m] || 0), 0);
                        
                        return (
                          <tr key={item.id} className={`border-t ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                            <td className={`py-2 px-4 ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                              <div className="flex flex-col">
                                <span className="font-medium text-xs">{item.name || item.account_name}</span>
                                {item.account_code && (
                                  <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                                    {item.account_code}
                                  </span>
                                )}
                              </div>
                            </td>
                            {displayMonths.map((month, idx) => (
                              <td key={month} className="py-1 px-1">
                                <Input
                                  type="number"
                                  value={item[month] || 0}
                                  onChange={(e) => handleLineItemChange(item.id, month, e.target.value)}
                                  className={`h-8 text-xs text-right font-mono w-full ${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-slate-200'}`}
                                />
                              </td>
                            ))}
                            <td className={`py-2 px-4 text-right font-mono font-medium ${
                              category === 'Sales' ? 'text-emerald-600' : 'text-red-600'
                            }`}>
                              {formatCurrency(itemTotal)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
      
      {/* Adjustment Dialog - Enhanced with Category & Sub-Account Level */}
      <Dialog open={adjustmentDialog} onOpenChange={(open) => {
        setAdjustmentDialog(open);
        if (open) {
          initializeCategoryAdjustments();
        } else {
          resetAdjustmentState();
        }
      }}>
        <DialogContent className={`sm:max-w-[700px] max-h-[85vh] overflow-y-auto ${isDark ? 'bg-zinc-900 border-zinc-800' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-violet-600" />
              Apply Bulk Adjustment
            </DialogTitle>
            <DialogDescription>
              Apply adjustments by category or individual sub-account.
            </DialogDescription>
          </DialogHeader>
          
          {/* Mode Tabs */}
          <Tabs value={adjustmentTab} onValueChange={setAdjustmentTab} className="mt-4">
            <TabsList className={`w-full ${isDark ? 'bg-zinc-800' : 'bg-slate-100'}`}>
              <TabsTrigger value="category" className="flex-1 rounded-sm">
                By Category
              </TabsTrigger>
              <TabsTrigger value="subaccount" className="flex-1 rounded-sm">
                By Sub-Account
              </TabsTrigger>
            </TabsList>
            
            {/* Quarter Selection - Shared */}
            <div className="mt-4 mb-4">
              <Label className={isDark ? 'text-zinc-300' : ''}>Apply to Quarters</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {QUARTERS.map(q => (
                  <button
                    key={q.key}
                    type="button"
                    onClick={() => {
                      const quarters = adjustmentData.quarters.includes(q.key)
                        ? adjustmentData.quarters.filter(qk => qk !== q.key)
                        : [...adjustmentData.quarters, q.key];
                      setAdjustmentData({ ...adjustmentData, quarters });
                    }}
                    className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${
                      adjustmentData.quarters.includes(q.key)
                        ? 'bg-violet-600 text-white border-violet-600'
                        : isDark ? 'border-zinc-700 text-zinc-400' : 'border-slate-200 text-slate-500'
                    }`}
                  >
                    {q.label} ({q.months[0]}-{q.months[2]})
                  </button>
                ))}
              </div>
              <p className={`text-xs mt-1 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                Select quarters or leave empty for all months
              </p>
            </div>
            
            {/* Category Tab */}
            <TabsContent value="category" className="mt-0">
              <div className={`border rounded-sm ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className={isDark ? 'bg-zinc-800' : 'bg-slate-50'}>
                      <th className="text-left py-2 px-3 font-medium">Category</th>
                      <th className="text-left py-2 px-3 font-medium w-32">Type</th>
                      <th className="text-right py-2 px-3 font-medium w-28">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CATEGORIES.map(cat => (
                      <tr key={cat} className={`border-t ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                        <td className={`py-2 px-3 ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                          {cat}
                        </td>
                        <td className="py-1 px-2">
                          <Select 
                            value={categoryAdjustments[cat]?.type || 'percentage'} 
                            onValueChange={(v) => setCategoryAdjustments(prev => ({
                              ...prev,
                              [cat]: { ...prev[cat], type: v }
                            }))}
                          >
                            <SelectTrigger className={`h-8 text-xs ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="percentage">Percent (%)</SelectItem>
                              <SelectItem value="fixed">Fixed (R)</SelectItem>
                              <SelectItem value="none">None</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-1 px-2">
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              value={categoryAdjustments[cat]?.value || 0}
                              onChange={(e) => setCategoryAdjustments(prev => ({
                                ...prev,
                                [cat]: { ...prev[cat], value: parseFloat(e.target.value) || 0 }
                              }))}
                              disabled={categoryAdjustments[cat]?.type === 'none'}
                              className={`h-8 text-xs text-right w-20 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
                            />
                            <span className={`text-xs w-4 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                              {categoryAdjustments[cat]?.type === 'percentage' ? '%' : categoryAdjustments[cat]?.type === 'fixed' ? 'R' : ''}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
            
            {/* Sub-Account Tab */}
            <TabsContent value="subaccount" className="mt-0 space-y-3">
              {/* Search */}
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`} />
                <Input
                  value={subAccountSearch}
                  onChange={(e) => setSubAccountSearch(e.target.value)}
                  placeholder="Search accounts..."
                  className={`pl-9 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
                />
              </div>
              
              {/* Accounts by Category */}
              <div className={`border rounded-sm max-h-[350px] overflow-y-auto ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                {CATEGORIES.map(cat => {
                  const categoryItems = filteredSubAccounts.filter(item => item.category === cat);
                  if (categoryItems.length === 0) return null;
                  
                  const isExpanded = expandedAdjustmentCategories.has(cat);
                  
                  return (
                    <div key={cat}>
                      {/* Category Header */}
                      <div
                        onClick={() => toggleAdjustmentCategory(cat)}
                        className={`flex items-center justify-between px-3 py-2 cursor-pointer ${
                          isDark ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-slate-50 hover:bg-slate-100'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <span className="font-medium text-sm">{cat}</span>
                          <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                            ({categoryItems.length} accounts)
                          </span>
                        </div>
                      </div>
                      
                      {/* Account Items */}
                      {isExpanded && (
                        <div className={`${isDark ? 'divide-zinc-700' : 'divide-slate-100'} divide-y`}>
                          {categoryItems.map(item => (
                            <div key={item.id} className={`flex items-center gap-2 px-3 py-2 pl-8 ${isDark ? 'bg-zinc-900' : 'bg-white'}`}>
                              <div className="flex-1 min-w-0">
                                <p className={`text-xs truncate ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                                  {item.account_name || item.name}
                                </p>
                                {item.account_code && (
                                  <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                                    {item.account_code}
                                  </p>
                                )}
                              </div>
                              <Select 
                                value={subAccountAdjustments[item.id]?.type || 'none'} 
                                onValueChange={(v) => setSubAccountAdjustments(prev => ({
                                  ...prev,
                                  [item.id]: { ...prev[item.id], type: v, value: prev[item.id]?.value || 0 }
                                }))}
                              >
                                <SelectTrigger className={`h-7 w-24 text-xs ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
                                  <SelectValue placeholder="None" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="percentage">Percent</SelectItem>
                                  <SelectItem value="fixed">Fixed</SelectItem>
                                  <SelectItem value="none">None</SelectItem>
                                </SelectContent>
                              </Select>
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  value={subAccountAdjustments[item.id]?.value || 0}
                                  onChange={(e) => setSubAccountAdjustments(prev => ({
                                    ...prev,
                                    [item.id]: { ...prev[item.id], value: parseFloat(e.target.value) || 0 }
                                  }))}
                                  disabled={!subAccountAdjustments[item.id]?.type || subAccountAdjustments[item.id]?.type === 'none'}
                                  className={`h-7 w-16 text-xs text-right ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
                                />
                                <span className={`text-xs w-3 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                                  {subAccountAdjustments[item.id]?.type === 'percentage' ? '%' : subAccountAdjustments[item.id]?.type === 'fixed' ? 'R' : ''}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                💡 Tip: Leave as "None" to skip adjustment for that account
              </p>
            </TabsContent>
          </Tabs>
          
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-dashed">
            <Button variant="outline" onClick={() => setAdjustmentDialog(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button 
              onClick={handleApplyAdjustment} 
              disabled={saving}
              className="bg-violet-600 hover:bg-violet-700 rounded-sm"
            >
              {saving ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Applying...</>
              ) : (
                <><Wand2 className="w-4 h-4 mr-2" />Apply Adjustments</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ForecastDetailPage;
